import { cleanFloat } from "@/lib/tool-kit/grades/rounding";

/**
 * Australian 7-point GPA and weighted average mark (WAM).
 *
 * Australia has no national grade scale: each university sets its own mark bands. The two
 * published schemes below differ in where High Distinction starts and in how a fail scores.
 * Sources (retrieved October 2026):
 * - "anu": Australian National University, Student assessment (coursework) policy, Table 1
 *   (HD 80–100, D 70–79, C 60–69, P 50–59, N 0–49) and Table 3 grade points (HD 7, D 6,
 *   CR 5, P 4, N 0), clause 64: "GPA = ∑ (grade point value x units) / ∑ units"
 *   (https://policies.anu.edu.au/ppl/document/ANUP_004603); ANU's GPA page says to calculate to
 *   three decimal points (https://www.anu.edu.au/students/program-administration/assessments-exams/grade-point-average-gpa).
 * - "qut": Queensland University of Technology, Manual of Policies and Procedures C/5.2 "Grading
 *   scales and awards", section 5: 7 High Distinction 85–100%, 6 Distinction 75–84%, 5 Credit
 *   65–74%, 4 Pass 50–64%, 3 Marginal Fail 40–49%, 2 Fail 25–39%, 1 Low Fail under 25%; section 8:
 *   numeric fail grades count toward the GPA (https://www.mopp.qut.edu.au/C/C_05_02.jsp).
 * - GPA weighting by unit value: University of Queensland, "Final grades" (my.UQ): GPA is the sum
 *   of grade × unit value over the sum of unit values, and UQ's example of grades 4, 5, 5, 7 for
 *   2, 2, 1, 1 units gives 5.0.
 * - WAM: each mark weighted by the unit's credit points and, at some universities, by level of
 *   study (University of Sydney, "Weighted Average Mark (WAM)"); the formula and which units are
 *   included differ by university, so an extra per-unit weight is an input and defaults to 1.
 *
 * "custom" lets the reader enter their own four mark cutoffs for HD, D, C and P; marks below the
 * P cutoff score 0, as at ANU.
 */

export type AusScheme = "anu" | "qut" | "custom";
export const AUS_SCHEMES: readonly AusScheme[] = ["anu", "qut", "custom"];

export type AusGrade = "HD" | "D" | "C" | "P" | "F" | "F3" | "F2" | "F1";

export interface AusBand {
  /** Lowest mark, inclusive. */
  min: number;
  grade: AusGrade;
  points: number;
}

const ANU: readonly AusBand[] = [
  { min: 80, grade: "HD", points: 7 },
  { min: 70, grade: "D", points: 6 },
  { min: 60, grade: "C", points: 5 },
  { min: 50, grade: "P", points: 4 },
  { min: 0, grade: "F", points: 0 },
];

const QUT: readonly AusBand[] = [
  { min: 85, grade: "HD", points: 7 },
  { min: 75, grade: "D", points: 6 },
  { min: 65, grade: "C", points: 5 },
  { min: 50, grade: "P", points: 4 },
  { min: 40, grade: "F3", points: 3 },
  { min: 25, grade: "F2", points: 2 },
  { min: 0, grade: "F1", points: 1 },
];

export interface AusCutoffs {
  hd: number;
  d: number;
  c: number;
  p: number;
}

export const DEFAULT_AUS_CUTOFFS: AusCutoffs = { hd: 85, d: 75, c: 65, p: 50 };

/** Cutoffs must lie in 1–100 and fall from HD to P. */
export function validAusCutoffs(cutoffs: AusCutoffs): boolean {
  const values = [cutoffs.hd, cutoffs.d, cutoffs.c, cutoffs.p];
  return (
    values.every(
      (value) => Number.isFinite(value) && value >= 1 && value <= 100
    ) &&
    cutoffs.hd > cutoffs.d &&
    cutoffs.d > cutoffs.c &&
    cutoffs.c > cutoffs.p
  );
}

export function ausBands(
  scheme: AusScheme,
  custom: AusCutoffs = DEFAULT_AUS_CUTOFFS
): readonly AusBand[] {
  if (scheme === "anu") {
    return ANU;
  }
  if (scheme === "qut") {
    return QUT;
  }
  return [
    { min: custom.hd, grade: "HD", points: 7 },
    { min: custom.d, grade: "D", points: 6 },
    { min: custom.c, grade: "C", points: 5 },
    { min: custom.p, grade: "P", points: 4 },
    { min: 0, grade: "F", points: 0 },
  ];
}

export function isValidAusMark(mark: number): boolean {
  return Number.isFinite(mark) && mark >= 0 && mark <= 100;
}

export function ausBand(
  mark: number,
  bands: readonly AusBand[]
): AusBand | null {
  if (!isValidAusMark(mark)) {
    return null;
  }
  return bands.find((band) => mark >= band.min) ?? null;
}

export interface AusUnit {
  mark: number;
  /** Credit points (unit value). */
  creditPoints: number;
  /** Extra WAM weight, e.g. a level weighting; 1 when none. */
  weight: number;
}

export interface AusSummary {
  /** Σ(mark × credit points × weight) ÷ Σ(credit points × weight). */
  wam: number;
  /** Σ(grade points × credit points) ÷ Σ credit points. */
  gpa: number;
  wamBand: AusBand;
  totalCreditPoints: number;
  /** Credit points of units that scored under a pass (grade F, F3, F2 or F1). */
  failedCreditPoints: number;
}

export function summariseAus(
  units: readonly AusUnit[],
  bands: readonly AusBand[]
): AusSummary | null {
  let cp = 0;
  let weightedCp = 0;
  let markSum = 0;
  let pointSum = 0;
  let failedCp = 0;
  for (const unit of units) {
    const band = ausBand(unit.mark, bands);
    if (band && unit.creditPoints > 0 && unit.weight > 0) {
      cp += unit.creditPoints;
      weightedCp += unit.creditPoints * unit.weight;
      markSum += unit.mark * unit.creditPoints * unit.weight;
      pointSum += band.points * unit.creditPoints;
      if (band.grade.startsWith("F")) {
        failedCp += unit.creditPoints;
      }
    }
  }
  if (cp <= 0) {
    return null;
  }
  const wam = cleanFloat(markSum / weightedCp);
  const wamBand = ausBand(wam, bands);
  if (wamBand === null) {
    return null;
  }
  return {
    wam,
    gpa: cleanFloat(pointSum / cp),
    wamBand,
    totalCreditPoints: cp,
    failedCreditPoints: failedCp,
  };
}
